/***********************
 * SMALL DIAGRAM ACTIVITY
 ************************/

import React, { useEffect, useState, useContext, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"

import GlobalState from "contexts/GlobalState"

import { Col, Row, CardBody } from "reactstrap"

import { Link } from "react-router-dom"
import ApexCharts from "apexcharts"
import ReactApexChart from "react-apexcharts"

// Locale/weekday related imports
import fi from "apexcharts/dist/locales/fi.json"
import se from "apexcharts/dist/locales/se.json"
import en from "apexcharts/dist/locales/en.json"

const SmallDiagramActivity = props => {
  const [chartData, setChartData] = useState([])
  const [chartDataDoor, setChartDataDoor] = useState([])
  const [chartDataMicro, setChartDataMicro] = useState([])
  const [chartDataWc, setChartDataWc] = useState([])
  const [chartDataBed, setChartDataBed] = useState([])
  const [chartDataFridge, setChartDataFridge] = useState([])
  const [chartDataCoffee, setChartDataCoffee] = useState([])

  // Language
  const [state] = useContext(GlobalState)
  const [language, setLanguage] = useState("fi")

  // Loading state
  const [isLoading, setIsLoading] = useState(true)

  // Muokkaamattomien käyttömäärien ylläpito
  const origMapsRef = useRef({})

  /**
   * Muodostaa kaaviossa käytettävän datan ja tallentaa alkuperäiset arvot refiin.
   *
   * Käyttötarkoitus:
   *  - Skaalaa dataa kaavion visualisointia varten.
   *  - Alkuperäiset mittausarvot talletetaan `origMapsRef`-referenssiin, jotta
   *    niitä voidaan myöhemmin käyttää esim. tooltipissa, päivityksissä tai analyyseissa.
   *
   * @param {Array<[string, number]>} data - Lähdedata (aikaleima, arvo)
   * @param {{ key: string, upTo: number }} opts
   *   - key: tunniste, jolla data tallennetaan `origMapsRef.current[key]`
   *   - upTo: arvo, johon aktiiviset datapisteet (y > 0) skaalataan näyttöä varten
   * @returns {Array<[string, number]>} Kaaviossa piirrettävä data, jossa aktiiviset arvot korvattu upTo-arvolla
   */
  function buildSeries(data, { key, upTo }) {
    const src = (data ?? []).filter(([, y]) => y !== 0)
    origMapsRef.current[key] = new Map(src.map(([x, y]) => [String(x), y]))
    return src.map(([x, y]) => [x, y > 0 ? upTo : y])
  }

  /**
   * Yhdistää peräkkäiset aktiiviset 5 minuutin slotit yhdeksi slotiksi.
   *
   * Käyttötarkoitus:
   *  - Käsittelee aktiivisuusdatan muodossa [ISO-aikaleima, arvo].
   *  - Jos useampi peräkkäinen aikaväli (5 min) on aktiivinen (arvo > 0),
   *    nämä yhdistetään yhdeksi slotiksi.
   *  - Jokaisesta käynnistä palautetaan yksi datapiste, jossa:
   *      x = aikaleima käynnin alusta, keskeltä tai lopusta (parametrin mukaan)
   *      y = aktiivisuusarvojen pyöristetty keskiarvo
   *
   * @param {Array<[string, number]>} data - Aktiivisuusdata 5 min välein
   * @param {"start" | "middle" | "end"} [position="middle"] - Mistä kohtaa käyntiä aikaleima otetaan
   * @returns {Array<[string, number]>} Lista yhdistettyjä datapisteitä muodossa [ISO-aikaleima, keskiarvo]
   */
  const mergeActiveSlotsToOneSlot = (data, position = "middle") => {
    if (!data?.length) return []

    const sorted = [...data].sort((a, b) => new Date(a[0]) - new Date(b[0]))

    const usage = []
    let inUse = false
    let startTs = 0
    let endTs = 0
    let sum = 0
    let count = 0

    for (let i = 0; i < sorted.length; i++) {
      const [iso, val] = sorted[i]
      const ts = new Date(iso).getTime()
      const active = val > 0

      if (active) {
        // Aloitetaan uusi käyttö, jos ei olla jo käytössä
        if (!inUse) {
          inUse = true
          startTs = ts
          sum = 0
          count = 0
        }
        // Kertyvä summa ja laskuri keskiarvoa varten
        sum += val
        count++
        endTs = ts
      }

      // Tarkistetaan, loppuuko aktiivinen käyttöjakso
      const nextActive = i < sorted.length - 1 ? sorted[i + 1][1] > 0 : false
      if (inUse && (!nextActive || i === sorted.length - 1)) {
        // Käyttö päättyy → muodostetaan yksi yhdistetty piste
        let pointTs
        if (position === "start") pointTs = startTs
        else if (position === "end") pointTs = endTs
        else pointTs = (startTs + endTs) / 2 // oletus: keskeltä

        const avg = Math.round(sum / count)
        usage.push([new Date(pointTs).toISOString(), avg])
        inUse = false
      }
    }

    return usage
  }

  useEffect(() => {
    setIsLoading(true)
    if (props.data != 0 && props.data != undefined) {
      // All activity
      let filterEmpty1 = props.data
      setChartData(filterEmpty1)

      // Bed, Kasvatetaan diagrammissa näytettävää käyttömäärää
      const renderedBedValue = 10
      const bedData = (props.bedData ?? []).map(([x, y]) => [
        x,
        y > 0 ? renderedBedValue : y,
      ])
      setChartDataBed(bedData)

      // Coffee
      const renderedCoffeeValue = 4
      let coffeeData = (props.coffeeData ?? []).map(([x, y]) => [
        x,
        y > 0 ? renderedCoffeeValue : y,
      ])
      setChartDataCoffee(mergeActiveSlotsToOneSlot(coffeeData, "start"))

      // WC
      setChartDataWc(mergeActiveSlotsToOneSlot(props.toilet, "middle"))

      // OutsideDoor. upTo = rendreöitävä käyttömäärä diagrammissa
      const renderedDoorValue = 3
      setChartDataDoor(
        buildSeries(props.doorData, { key: "door", upTo: renderedDoorValue })
      )

      // Micro
      const renderedWcValue = 2
      setChartDataMicro(
        buildSeries(props.microData, { key: "micro", upTo: renderedWcValue })
      )

      // Fridge
      const renderedFridgeValue = 1
      setChartDataFridge(
        buildSeries(props.fridgeData, {
          key: "fridge",
          upTo: renderedFridgeValue,
        })
      )
    }
    setIsLoading(false)
  }, [
    props.data,
    props.doorData,
    props.bedData,
    props.toilet,
    props.fridgeData,
    props.coffeeData,
  ])

  // Init language
  useEffect(() => {
    const currentLanguage = localStorage.getItem("i18nextLng")
    if (currentLanguage) {
      if (currentLanguage.startsWith("fi")) {
        setLanguage("fi")
      } else if (currentLanguage.startsWith("sv")) {
        setLanguage("se")
      } else {
        setLanguage("en")
      }
    }
  }, [])

  // Language change
  useEffect(() => {
    switch (state.language) {
      case "fi":
        setLanguage("fi")
        ApexCharts.exec("small-chart", "setLocale", "fi")
        break
      case "en":
        setLanguage("en")
        ApexCharts.exec("small-chart", "setLocale", "en")
        break
      case "sv":
        setLanguage("se")
        ApexCharts.exec("small-chart", "setLocale", "se")
        break
    }
  }, [state.language])

  const series = [
    {
      name: props.t("Bed"),
      value: props.t("Bed"),
      data: chartDataBed,
      type: "area",
    },
    {
      name:
        props.t("Coffee") +
        (props.coffeeTimes ? ` (${props.coffeeTimes})` : ""),
      value: props.t("Coffee"),
      data: chartDataCoffee,
      type: "scatter",
      _key: "coffee",
    },
    {
      name: props.t("All activity"),
      value: props.t("All activity"),
      data: chartData,
      type: "area",
    },
    {
      name: props.t("WC") + (props.wcTimes ? ` (${props.wcTimes})` : ""),
      value: props.t("WC"),
      data: chartDataWc,
      type: "scatter",
      _key: "wc",
    },
    {
      name:
        props.t("Outside door opened") +
        (props.doorTimes ? ` (${props.doorTimes})` : ""),
      value: props.t("Outside door opened"),
      data: chartDataDoor,
      type: "scatter",
      _key: "door",
    },
    {
      name:
        props.t("Micro") + (props.microTimes ? ` (${props.microTimes})` : ""),
      value: props.t("Micro"),
      data: chartDataMicro,
      type: "scatter",
      _key: "micro",
    },
    {
      name:
        props.t("Fridge") +
        (props.fridgeTimes ? ` (${props.fridgeTimes})` : ""),
      value: props.t("Fridge"),
      data: chartDataFridge,
      type: "scatter",
      _key: "fridge",
    },
  ]
  const settingToolbar = t => {
    const isHome = t === "home"

    return {
      show: isHome,
      offsetX: 0,
      offsetY: 0,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
        customIcons: [],
      },
      export: {
        csv: {
          filename: undefined,
          columnDelimiter: " ",
          headerCategory: "category",
          headerValue: "value",
          dateFormatter(timestamp) {
            return new Date(timestamp).toDateString()
          },
        },
        svg: {
          filename: undefined,
        },
        png: {
          filename: undefined,
        },
      },
      autoSelected: "zoom",
    }
  }
  const [options] = useState({
    chart: {
      id: "small-chart",
      locales: [fi, en, se],
      defaultLocale: language,
      toolbar: settingToolbar(props.type),
      stacked: false,
    },
    dataLabels: { enabled: !1 },
    // Bed, Coffee, activity, WC, door, Micro, Fridge
    stroke: { curve: "smooth", width: [2, 0, 2, 0, 0, 0, 0] },

    // plotOptions: {
    //   bar: {
    //     columnWidth: "50%",
    //   },
    // },
    markers: {
      // Bed, Coffee, activity, WC, door, Micro, Fridge
      size: [5, 5, 5, 6, 5, 5, 5],
      colors: [
        "#a6a6a600", // Bed
        "#A04000", // Coffee
        "#48d0f800", // Activity
        "#ff9b00", // WC
        "#ffffff", // Outside Door
        "#2ECC71", // Micro
        "#9B59B6", // Fridge
      ],
      strokeWidth: 1,
      strokeColors: [
        "#a6a6a600", // Bed
        "#A04000", // Coffee
        "#48d0f800", // Activity
        "#ff9b00", // WC
        "#000000", // Outside Door
        "#2ECC71", // Micro
        "#9B59B6", // Fridge
      ],
      // https://htmlcolorcodes.com/color-chart/
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
      },
    },
    legend: {
      show: true,
    },
    tooltip: {
      followCursor: true,
      shared: false,
      intersect: true,
      x: {
        format: "dd.MM.yyyy HH:mm",
      },
      custom: function (opts) {
        const si = opts.seriesIndex
        const dpi = opts.dataPointIndex
        const desc = opts.w.config.series[si].value
        const key = opts.w.config.series[si]._key
        const point = opts.w.config.series[si].data[dpi]
        const x = Array.isArray(point) ? point[0] : point?.x
        const value = Array.isArray(point) ? point[1] : point?.y

        // hae alkuperäinen arvo oikeasta mapista
        const map = origMapsRef.current[key]
        const original =
          x != null && map?.has(String(x)) ? map.get(String(x)) : value

        if (desc == props.t("Bed")) {
          if (value > 0) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              props.t("in use") +
              "</span>" +
              "</div>"
            )
          }
          if (value == 0) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              props.t("not in use") +
              "</span>" +
              "</div>"
            )
          }
        }
        if (desc == props.t("All activity")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            value +
            " " +
            "%" +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("WC")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            props.t("Activity in WC") +
            " " +
            original +
            " " +
            "%" +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Micro")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            props.t("in use") +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Outside door opened")) {
          if (original == 1) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              "</span>" +
              "</div>"
            )
          } else {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              desc +
              " " +
              original +
              " " +
              props.t("times") +
              "</span>" +
              "</div>"
            )
          }
        }
        if (desc == props.t("Coffee")) {
          return (
            '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
            "<span>" +
            desc +
            " " +
            props.t("in use") +
            "</span>" +
            "</div>"
          )
        }
        if (desc == props.t("Fridge")) {
          if (original == 1) {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              props.t("Fridge opened") +
              "</span>" +
              "</div>"
            )
          } else {
            return (
              '<div class="py-2 me-2 ms-2 text-muted font-size-12">' +
              "<span>" +
              props.t("Fridge opened") +
              " " +
              original +
              " " +
              props.t("times") +
              "</span>" +
              "</div>"
            )
          }
        }
      },
    },
    //
    colors: [
      "#a6a6a688", // Bed
      "#A04000", // Coffee
      "#48d0f8", // Activity
      "#FF6600", // WC
      "#FFFFFF", // Outside Door
      "#2ECC71", // Micro
      "#9B59B6", // Fridge
    ],
    fill: {
      // no effect in scatters
      opacity: [1, 1, 1, 1, 1, 1, 1],
      // bed, coffee, activity, wc, outsideDoor, micro, fridge
      type: ["solid", "solid", "gradient", "solid", "solid", "solid", "solid"],
      gradient: {
        type: "vertical",
        shade: "light",
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.75,
        stops: [0, 90, 100],
      },
      // solid: {
      //   color: "#ff9b00",
      //   width: 32,
      //   height: 32,
      // },
    },
  })

  return (
    <div>
      {isLoading ? (
        <Row>
          <Col xs="12">
            <CardBody className="text-center" style={{ height: props.size }}>
              <div className="py-5">
                <Link to="#" className="text-primary">
                  <i className="bx bx-loader bx-spin font-size-18 align-middle me-2" />
                  {props.t("Loading")}
                </Link>
              </div>
            </CardBody>
          </Col>
        </Row>
      ) : (
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={props.size}
        />
      )}
    </div>
  )
}

SmallDiagramActivity.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
  doorData: PropTypes.any,
  microData: PropTypes.any,
  bedData: PropTypes.any,
  fridgeData: PropTypes.any,
  coffeeData: PropTypes.any,
  size: PropTypes.number,
  toilet: PropTypes.any,
  type: PropTypes.string,
  doorTimes: PropTypes.any,
  microTimes: PropTypes.any,
  wcTimes: PropTypes.any,
  fridgeTimes: PropTypes.any,
  coffeeTimes: PropTypes.any,
}

export default withTranslation()(SmallDiagramActivity)
